import { useId, useState } from 'react';
import { Modal } from '../../components/Modal';
import type { SlideThemeCatalogRead } from '../../types/api';
import { useBrandDefaultTheme } from '../knowledge-base/useBrandDefaultTheme';
import { ThemeGallery, ThemePreview } from './ThemeGallery';
import { useSlideThemes } from './useSlideThemes';

/** Looks on the strip after Auto: the brand default first, then the best vibe matches. */
const STRIP_SIZE = 5;

/**
 * The strip's looks: the brand default, then the rest ranked by how well their
 * vibe tags fit (tagged for this vibe, then untagged, then the others), in
 * catalogue order within a rank. A chosen look that did not make the cut takes
 * the last slot, so the selection is always on screen.
 */
function stripThemes(
  themes: readonly SlideThemeCatalogRead[],
  defaultId: string | null,
  vibeId: string | null,
  selectedId: string | null,
): SlideThemeCatalogRead[] {
  const rank = (theme: SlideThemeCatalogRead) =>
    vibeId && theme.vibe_ids.includes(vibeId) ? 0 : theme.vibe_ids.length === 0 ? 1 : 2;
  const brandDefault = themes.find((theme) => theme.id === defaultId) ?? null;
  const others = themes
    .map((theme, index) => ({ theme, index }))
    .filter(({ theme }) => theme.id !== defaultId)
    .sort((left, right) => rank(left.theme) - rank(right.theme) || left.index - right.index)
    .map(({ theme }) => theme);
  let strip = (brandDefault ? [brandDefault, ...others] : others).slice(0, STRIP_SIZE);
  const selected = themes.find((theme) => theme.id === selectedId) ?? null;
  if (selected && !strip.includes(selected)) {
    strip = [...strip.slice(0, STRIP_SIZE - 1), selected];
  }
  return strip;
}

interface LookPickerProps {
  businessId: string;
  /** Narrows the catalogue to the looks tagged for this vibe; null shows all of them. */
  vibeId: string | null;
  selected: SlideThemeCatalogRead | null;
  onSelect: (theme: SlideThemeCatalogRead | null) => void;
  disabled: boolean;
}

/**
 * The brief's Look: one row of small thumbnails — Auto, the brand default and
 * the four looks that best fit the tone — and "See all" for the whole gallery
 * in a wide dialog. Zoom and Set as brand default live in the dialog only.
 *
 * Shown whenever we are the one drawing, so a carousel and a generated single
 * both get it, and a post on the user's own photo does not. Auto is the default
 * and sends `theme_id: null`, which means the brand default, else classic.
 */
export function LookPicker({ businessId, vibeId, selected, onSelect, disabled }: LookPickerProps) {
  const { themes, isLoading, error } = useSlideThemes(vibeId);
  const brandDefault = useBrandDefaultTheme(businessId);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const groupName = useId();

  const strip = stripThemes(themes, brandDefault.defaultThemeId, vibeId, selected?.id ?? null);
  const defaultTheme = themes.find((theme) => theme.id === brandDefault.defaultThemeId) ?? null;
  const autoTitle = defaultTheme
    ? `Auto: renders in ${defaultTheme.name}, your brand default.`
    : 'Auto: your brand default style, or the classic one until you set it.';

  function select(themeId: string | null) {
    onSelect(themeId === null ? null : (themes.find((theme) => theme.id === themeId) ?? null));
  }

  return (
    <fieldset className="field look-picker" disabled={disabled}>
      <legend className="field__label">Visual style</legend>

      {(error ?? brandDefault.error) ? (
        <p className="form__error" role="alert">
          {error ?? brandDefault.error}
        </p>
      ) : null}

      {isLoading && themes.length === 0 ? (
        <p className="kb-section__status" role="status">
          Loading the visual styles
        </p>
      ) : themes.length === 0 ? (
        <p className="field__hint">No visual styles added yet. This post uses the classic style.</p>
      ) : (
        <div className="look-picker__row">
          <label
            className={selected === null ? 'look-thumb look-thumb--selected' : 'look-thumb'}
            title={autoTitle}
          >
            <input
              className="visually-hidden"
              type="radio"
              name={groupName}
              value=""
              checked={selected === null}
              onChange={() => select(null)}
            />
            <span className="look-thumb__auto" aria-hidden="true">
              Auto
            </span>
            <span className="look-thumb__name">Auto</span>
          </label>

          {strip.map((theme) => {
            const isSelected = theme.id === selected?.id;
            const isDefault = theme.id === brandDefault.defaultThemeId;
            return (
              <label
                className={isSelected ? 'look-thumb look-thumb--selected' : 'look-thumb'}
                key={theme.id}
                title={theme.description}
              >
                <input
                  className="visually-hidden"
                  type="radio"
                  name={groupName}
                  value={theme.id}
                  checked={isSelected}
                  onChange={() => select(theme.id)}
                />
                <ThemePreview theme={theme} />
                <span className="look-thumb__name">
                  {theme.name}
                  {isDefault ? <span className="look-thumb__badge">Default</span> : null}
                </span>
              </label>
            );
          })}

          <button
            className="button look-picker__all"
            type="button"
            onClick={() => setIsGalleryOpen(true)}
          >
            See all {themes.length}
          </button>
        </div>
      )}

      {themes.length > 0 ? (
        <p className="field__hint look-picker__about">
          {selected ? `${selected.name}: ${selected.description}` : autoTitle}
        </p>
      ) : null}

      {brandDefault.notice ? (
        <p className="look-field__notice" role="status">
          {brandDefault.notice}
        </p>
      ) : null}

      {isGalleryOpen ? (
        <Modal
          title="Choose a visual style"
          kind="look"
          size="wide"
          onDismiss={() => setIsGalleryOpen(false)}
        >
          <p className="modal__message">
            The palette, the band behind the caption and the card under it. We still pick the
            layouts.
          </p>
          <ThemeGallery
            themes={themes}
            selectedId={selected?.id ?? null}
            onSelect={select}
            brandDefaultId={brandDefault.defaultThemeId}
            onSetBrandDefault={brandDefault.setDefault}
            allowAuto
            disabled={disabled}
          />
          <div className="look-picker__done">
            <button
              className="button button--primary"
              type="button"
              onClick={() => setIsGalleryOpen(false)}
            >
              Done
            </button>
          </div>
        </Modal>
      ) : null}
    </fieldset>
  );
}
